import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Spinner, Button } from 'react-bootstrap';
import QuestionForm, { QuestionFormData } from '../../components/QuestionForm';
import api from '../../services/api';
import { notify } from '../../stores/useNotificationStore';

const QuestionsPage: React.FC = () => {
  const [editingQuestion, setEditingQuestion] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    setInitialLoading(true);
    api.getQuestion(parseInt(id)).then(res => {
      if (res.success && res.data) {
        setEditingQuestion(res.data);
      } else {
        notify.error('Pergunta não encontrada.');
        navigate('/app/perguntas/list');
      }
    }).finally(() => setInitialLoading(false));
  }, [id, navigate]);

  const handleSubmit = async (data: QuestionFormData, equivalencias: number[]) => {
    setLoading(true);
    try {
      let savedId: number | null = null;

      if (id && editingQuestion?.id) {
        // Update
        const res = await api.updateQuestion(editingQuestion.id, data as any);
        if (res.success) {
          savedId = editingQuestion.id;
        } else {
          throw new Error((res.data as any)?.error || 'Erro ao atualizar.');
        }
      } else {
        // Create
        const res = await api.createQuestion(data as any);
        if (res.success && res.data) {
          savedId = (res.data as any).id;
        } else {
          throw new Error((res.data as any)?.error || 'Erro ao criar.');
        }
      }

      if (savedId) {
        // Sync equivalencias: remove existing, add new
        if (id && editingQuestion?.id) {
          const existingEquivRes = await api.getEquivalenciasByPergunta(savedId);
          const existing = existingEquivRes.success ? (existingEquivRes.data || []) as any[] : [];
          const existingIds = existing.map((eq: any) => eq.pergunta_id_2).filter(Boolean);

          // Remove those not in new list
          for (const eq of existing) {
            if (!equivalencias.includes(eq.pergunta_id_2)) {
              await api.deleteEquivalencia(eq.id);
            }
          }
          // Add new ones
          for (const eqId of equivalencias) {
            if (!existingIds.includes(eqId)) {
              await api.createEquivalence({ pergunta_id_1: savedId, pergunta_id_2: eqId });
            }
          }
        } else {
          // New question: just add all
          for (const eqId of equivalencias) {
            await api.createEquivalence({ pergunta_id_1: savedId, pergunta_id_2: eqId });
          }
        }

        notify.success(id ? 'Pergunta atualizada com sucesso!' : 'Pergunta criada com sucesso!');
        navigate('/app/perguntas/list');
      }
    } catch (err: any) {
      notify.error(err?.message || 'Erro ao salvar pergunta.');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex align-items-center gap-3 mb-4">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/app/perguntas/list')}>
          ← Voltar
        </Button>
        <h2 className="mb-0">{id ? 'Editar Pergunta' : 'Cadastro de Perguntas'}</h2>
      </div>
      <QuestionForm
        onSubmit={handleSubmit}
        questionToEdit={editingQuestion}
        isLoading={loading}
      />
    </Container>
  );
};

export default QuestionsPage;
